/**
 * Variables Section Component
 * Allows users to define custom variables for prompt personalization.
 * Uses shadcn theme tokens for dark/light mode support.
 */

import React, { useState } from 'react'
import { Plus, X, Info, Edit2, Check, Shield, Search, Database, Fingerprint, Brain, Sparkles, Lightbulb } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Switch } from '../../ui/switch'

interface VariableConfig {
  default: string
  required: boolean
  auto_filter: boolean
  auto_train: boolean
  identity?: boolean
  memory_key?: boolean
  auto_learn?: boolean
  suggestions?: boolean
  description?: string
}

type VariableValue = string | VariableConfig

interface VariablesSectionProps {
  formData: any
  onChange: (updates: any) => void
}

const normalizeVariable = (value: VariableValue): VariableConfig => {
  if (typeof value === 'string') {
    return { default: value, required: false, auto_filter: false, auto_train: false }
  }
  return {
    default: value.default || '',
    required: value.required || false,
    auto_filter: value.auto_filter || false,
    auto_train: value.auto_train || false,
    identity: value.identity || false,
    memory_key: value.memory_key || false,
    auto_learn: value.auto_learn || false,
    suggestions: value.suggestions || false,
    description: value.description || '',
  }
}

export const VariablesSection: React.FC<VariablesSectionProps> = ({ formData, onChange }) => {
  const [isAdding, setIsAdding] = useState(false)
  const [editingVar, setEditingVar] = useState<string | null>(null)
  const [varName, setVarName] = useState('')
  const [varDefault, setVarDefault] = useState('')
  const [varRequired, setVarRequired] = useState(false)
  const [varAutoFilter, setVarAutoFilter] = useState(false)
  const [varAutoTrain, setVarAutoTrain] = useState(false)
  const [editDefault, setEditDefault] = useState('')
  const [editRequired, setEditRequired] = useState(false)
  const [editAutoFilter, setEditAutoFilter] = useState(false)
  const [editAutoTrain, setEditAutoTrain] = useState(false)
  const [editIdentity, setEditIdentity] = useState(false)
  const [editMemoryKey, setEditMemoryKey] = useState(false)
  const [editAutoLearn, setEditAutoLearn] = useState(false)
  const [editSuggestions, setEditSuggestions] = useState(false)

  const rawVariables: Record<string, VariableValue> = (formData.settings?.variables as any) || {}
  const variables: Record<string, VariableConfig> = Object.fromEntries(
    Object.entries(rawVariables).map(([k, v]) => [k, normalizeVariable(v)])
  )

  const upsertVariables = (next: Record<string, VariableConfig>) => {
    const settings = formData.settings || {}
    onChange({ settings: { ...settings, variables: next } })
  }

  // Exclusive toggles — only one variable can have each
  const toggleExclusive = (name: string, flag: 'identity' | 'memory_key' | 'auto_learn' | 'suggestions') => {
    const isCurrent = variables[name]?.[flag]
    const next = { ...variables }
    Object.keys(next).forEach(k => {
      next[k] = { ...next[k], [flag]: k === name ? !isCurrent : false }
    })
    upsertVariables(next)
  }

  const addVariable = () => {
    const name = varName.trim()
    if (!name) return
    const next = { ...variables, [name]: { default: varDefault, required: varRequired, auto_filter: varAutoFilter, auto_train: varAutoTrain } }
    upsertVariables(next)
    setVarName(''); setVarDefault(''); setVarRequired(false); setVarAutoFilter(false); setVarAutoTrain(false); setIsAdding(false)
  }

  const removeVariable = (name: string) => {
    const next = { ...variables }
    delete next[name]
    upsertVariables(next)
  }

  const startEditVariable = (name: string) => {
    const config = variables[name]
    setEditingVar(name)
    setEditDefault(config.default || '')
    setEditRequired(config.required || false)
    setEditAutoFilter(config.auto_filter || false)
    setEditAutoTrain(config.auto_train || false)
    setEditIdentity(config.identity || false)
    setEditMemoryKey(config.memory_key || false)
    setEditAutoLearn(config.auto_learn || false)
    setEditSuggestions(config.suggestions || false)
  }

  const saveEditVariable = () => {
    if (editingVar) {
      const next = { ...variables }
      // Exclusive flags — clear from all others when toggling ON
      for (const flag of ['identity', 'memory_key', 'auto_learn', 'suggestions'] as const) {
        const editVal = { identity: editIdentity, memory_key: editMemoryKey, auto_learn: editAutoLearn, suggestions: editSuggestions }[flag]
        if (editVal) {
          Object.keys(next).forEach(k => { next[k] = { ...next[k], [flag]: false } })
        }
      }
      next[editingVar] = {
        default: editDefault, required: editRequired, auto_filter: editAutoFilter, auto_train: editAutoTrain,
        identity: editIdentity, memory_key: editMemoryKey, auto_learn: editAutoLearn, suggestions: editSuggestions,
      }
      upsertVariables(next)
      setEditingVar(null)
    }
  }

  const handleVarKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); addVariable() } }
  const handleEditKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); saveEditVariable() } else if (e.key === 'Escape') setEditingVar(null) }

  const varEntries = Object.entries(variables)

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          Variables <span className="font-normal text-muted-foreground">(use as {`{{variable}}`} in prompts)</span>
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Define variables to personalize the system context and isolate user data
        </p>
      </div>

      {/* Collapsible info */}
      <details className="group rounded-lg border bg-muted/50 text-sm">
        <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none text-muted-foreground hover:text-foreground">
          <Info className="w-4 h-4 shrink-0" />
          <span className="font-medium">Variable options explained</span>
        </summary>
        <div className="px-3 pb-3 pt-0 text-xs text-muted-foreground space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-start gap-2">
              <Shield className="w-3.5 h-3.5 mt-0.5 text-red-500 shrink-0" />
              <span><strong className="text-foreground/80">Required</strong> — must be in every request</span>
            </div>
            <div className="flex items-start gap-2">
              <Search className="w-3.5 h-3.5 mt-0.5 text-purple-500 shrink-0" />
              <span><strong className="text-foreground/80">Auto Filter</strong> — filter KB search by this</span>
            </div>
            <div className="flex items-start gap-2">
              <Database className="w-3.5 h-3.5 mt-0.5 text-green-500 shrink-0" />
              <span><strong className="text-foreground/80">Auto Train</strong> — attach to training data</span>
            </div>
            <div className="flex items-start gap-2">
              <Fingerprint className="w-3.5 h-3.5 mt-0.5 text-blue-500 shrink-0" />
              <span><strong className="text-foreground/80">Identity</strong> — user identity for analytics</span>
            </div>
            <div className="flex items-start gap-2">
              <Brain className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
              <span><strong className="text-foreground/80">Memory Key</strong> — persistent memory scoped by this value</span>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 text-cyan-500 shrink-0" />
              <span><strong className="text-foreground/80">Auto Learn</strong> — learn about users from conversations, scoped by this value</span>
            </div>
            <div className="flex items-start gap-2">
              <Lightbulb className="w-3.5 h-3.5 mt-0.5 text-orange-500 shrink-0" />
              <span><strong className="text-foreground/80">Suggestions</strong> — generate improvement suggestions from conversations</span>
            </div>
          </div>
          <p className="text-xs">Enable all three for a "user_id" variable to ensure each user only sees their own data.</p>
        </div>
      </details>

      {/* Variables list */}
      {varEntries.length > 0 && (
        <div className="space-y-2">
          {varEntries.map(([name, config]) => (
            <div key={name} className="flex items-center gap-3 rounded-lg border p-3 bg-card">
              {/* Name */}
              <div className="min-w-0 flex-1">
                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground">{`{{${name}}}`}</span>
                {config.default && (
                  <span className="ml-2 text-xs text-muted-foreground">= {config.default}</span>
                )}
              </div>

              {/* Flag icons */}
              <div className="flex items-center gap-1.5 shrink-0">
                {config.required && (
                  <span className="p-1 rounded bg-red-500/10" title="Required">
                    <Shield className="w-3 h-3 text-red-500" />
                  </span>
                )}
                {config.auto_filter && (
                  <span className="p-1 rounded bg-purple-500/10" title="Auto Filter">
                    <Search className="w-3 h-3 text-purple-500" />
                  </span>
                )}
                {config.auto_train && (
                  <span className="p-1 rounded bg-green-500/10" title="Auto Train">
                    <Database className="w-3 h-3 text-green-500" />
                  </span>
                )}
                <button
                  onClick={() => toggleExclusive(name, 'identity')}
                  className={`p-1 rounded transition ${config.identity ? 'bg-blue-500/10 text-blue-500' : 'text-muted-foreground/30 hover:text-blue-400'}`}
                  title={config.identity ? 'Identity variable' : 'Set as identity'}
                >
                  <Fingerprint className="w-3 h-3" />
                </button>
                <button
                  onClick={() => toggleExclusive(name, 'memory_key')}
                  className={`p-1 rounded transition ${config.memory_key ? 'bg-amber-500/10 text-amber-500' : 'text-muted-foreground/30 hover:text-amber-400'}`}
                  title={config.memory_key ? 'Memory key (agent remembers per this value)' : 'Set as memory key'}
                >
                  <Brain className="w-3 h-3" />
                </button>
                <button
                  onClick={() => toggleExclusive(name, 'auto_learn')}
                  className={`p-1 rounded transition ${config.auto_learn ? 'bg-cyan-500/10 text-cyan-500' : 'text-muted-foreground/30 hover:text-cyan-400'}`}
                  title={config.auto_learn ? 'Auto-learn enabled (learns from conversations)' : 'Enable auto-learn'}
                >
                  <Sparkles className="w-3 h-3" />
                </button>
                <button
                  onClick={() => toggleExclusive(name, 'suggestions')}
                  className={`p-1 rounded transition ${config.suggestions ? 'bg-orange-500/10 text-orange-500' : 'text-muted-foreground/30 hover:text-orange-400'}`}
                  title={config.suggestions ? 'Suggestions enabled' : 'Enable suggestions'}
                >
                  <Lightbulb className="w-3 h-3" />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-0.5 shrink-0">
                <button onClick={() => startEditVariable(name)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => removeVariable(name)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit form */}
      {editingVar && (
        <div className="rounded-lg border-2 border-primary/20 bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium text-foreground">
            Edit <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{`{{${editingVar}}}`}</span>
          </p>
          <Input
            value={editDefault}
            onChange={e => setEditDefault(e.target.value)}
            onKeyDown={handleEditKey}
            placeholder="Default value"
          />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editRequired} onCheckedChange={setEditRequired} />
              <Shield className="w-3.5 h-3.5 text-red-500" /> Required
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editAutoFilter} onCheckedChange={setEditAutoFilter} />
              <Search className="w-3.5 h-3.5 text-purple-500" /> Auto Filter
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editAutoTrain} onCheckedChange={setEditAutoTrain} />
              <Database className="w-3.5 h-3.5 text-green-500" /> Auto Train
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editIdentity} onCheckedChange={setEditIdentity} />
              <Fingerprint className="w-3.5 h-3.5 text-blue-500" /> Identity
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editMemoryKey} onCheckedChange={setEditMemoryKey} />
              <Brain className="w-3.5 h-3.5 text-amber-500" /> Memory Key
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editAutoLearn} onCheckedChange={setEditAutoLearn} />
              <Sparkles className="w-3.5 h-3.5 text-cyan-500" /> Auto Learn
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={editSuggestions} onCheckedChange={setEditSuggestions} />
              <Lightbulb className="w-3.5 h-3.5 text-orange-500" /> Suggestions
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setEditingVar(null)}>Cancel</Button>
            <Button size="sm" onClick={saveEditVariable}>Save</Button>
          </div>
        </div>
      )}

      {/* Add form */}
      {isAdding ? (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium text-foreground">Add Variable</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Input
                value={varName}
                onChange={e => setVarName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                onKeyDown={handleVarKey}
                placeholder="Variable name (e.g., user_id)"
              />
              <p className="text-xs text-muted-foreground mt-1">Letters, numbers, underscores only</p>
            </div>
            <Input
              value={varDefault}
              onChange={e => setVarDefault(e.target.value)}
              onKeyDown={handleVarKey}
              placeholder="Default value (e.g., guest)"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={varRequired} onCheckedChange={setVarRequired} />
              <Shield className="w-3.5 h-3.5 text-red-500" /> Required
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={varAutoFilter} onCheckedChange={setVarAutoFilter} />
              <Search className="w-3.5 h-3.5 text-purple-500" /> Auto Filter
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={varAutoTrain} onCheckedChange={setVarAutoTrain} />
              <Database className="w-3.5 h-3.5 text-green-500" /> Auto Train
            </label>
          </div>
          {!varRequired && !varAutoFilter && !varAutoTrain && (
            <button
              type="button"
              onClick={() => { setVarRequired(true); setVarAutoFilter(true); setVarAutoTrain(true) }}
              className="text-xs text-primary hover:underline"
            >
              Enable all for multi-user data isolation
            </button>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setIsAdding(false); setVarName(''); setVarDefault(''); setVarRequired(false); setVarAutoFilter(false); setVarAutoTrain(false) }}>Cancel</Button>
            <Button size="sm" onClick={addVariable} disabled={!varName.trim()}>Add</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add Variable
        </Button>
      )}
    </div>
  )
}
